import { Pagination } from "types/Models/pagination";
import { MissingInfoResponse } from "types/Models/deadlineAlarm";
import { stringify } from "qs";
import { emptyApi } from "./emptyApi";
import { ChannelTask } from "./task";

export type Alarm = {
  entity_id: string;
  entity_name: string;
  rule: {
    name: string;
    tax_year_type: string;
  };
  deadline: string;
  task: ChannelTask;
};

export type FilingDeadline = {
  uuid: string;
  filing: string;
  entity: string;
  entity_uuid: string;
  group: string;
  group_uuid: string;
  season: string;
  status: string;
  source: "alarm" | "cart" | "task";
  deadline: string | null;
  deadline_status: "overdue" | "critical" | "upcoming" | null;
};

export type FilingsDashboardResponse = {
  total_count: number;
  total_pages: number;
  current_page: number;
  per_page: number;
  filings: FilingDeadline[];
};

export type AlarmRule = {
  uuid: string;
  name: string;
  display_name: string | null;
  formation_type: string | null;
  formation_type_name: string | null;
  taxed_as: number;
  us_state: string | null;
  us_state_name: string | null;
  date_type: number;
  deadline_date: number | null;
  deadline_day: number | null;
  deadline_month: number | null;
  first_deadline_logic: number;
  approx_price: string | null;
};

export type AlarmRulePayload = Omit<
  AlarmRule,
  "uuid" | "formation_type_name" | "us_state_name"
>;

export const alarmApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllAlarms: build.query<
      Pagination & { alarms: Alarm[] },
      {
        page_num: number;
        search_term?: string;
        company_tag_filter_uuids?: string;
        company_groups?: string;
        date_from?: string;
        date_to?: string;
      }
    >({
      query: (params) => {
        return {
          url: `/api/inkle/alarm/`,
          params,
        };
      },
      providesTags: () => ["ALARMS"],
    }),

    getFilingsDashboard: build.query<
      FilingsDashboardResponse,
      {
        page_num?: number;
        search_term?: string;
        status?: string[];
        deadline_status?: string;
        season?: string[];
        group_uuid?: string;
        entity_uuid?: string;
        filing_name?: string[];
        deadline_from?: string;
        deadline_to?: string;
      }
    >({
      query: (params) => {
        const queryString = stringify(params, {
          arrayFormat: "comma",
          skipNulls: true,
        });
        return {
          url: `/api/inkle/alarm/filings/dashboard/?${queryString}`,
        };
      },
      providesTags: () => ["FILINGS_DASHBOARD"],
    }),
    getAdminMissingInfo: build.query<
      MissingInfoResponse,
      {
        searchTerm?: string;
        groupUuids?: string[];
        pageNum?: number;
        missingInfoTypes?: string[];
      }
    >({
      query: ({ searchTerm, groupUuids, pageNum, missingInfoTypes }) => {
        const params = stringify(
          {
            search_term: searchTerm || null,
            group_uuids: groupUuids || null,
            page_num: pageNum || null,
            missing_info_types: missingInfoTypes || null,
          },
          { skipNulls: true, addQueryPrefix: true, arrayFormat: "repeat" }
        );
        return { url: `/api/inkle/alarm/admin/missing_info/${params}` };
      },
      providesTags: ["GROUP_MISSING_INFO"],
    }),

    getAlarmRules: build.query<
      Pagination & { rules: AlarmRule[] },
      { us_state_uuid?: string; search_term?: string; page_num?: number }
    >({
      query: (params) => ({
        url: `/api/inkle/alarm/rules/`,
        params,
      }),
      providesTags: ["ALARM_RULES"],
    }),

    createAlarmRule: build.mutation<AlarmRule, Partial<AlarmRulePayload>>({
      query: (body) => ({
        url: `/api/inkle/alarm/rules/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ALARM_RULES"],
    }),

    updateAlarmRule: build.mutation<
      AlarmRule,
      { ruleUuid: string; body: Partial<AlarmRulePayload> }
    >({
      query: ({ ruleUuid, body }) => ({
        url: `/api/inkle/alarm/rules/${ruleUuid}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["ALARM_RULES"],
    }),
  }),
});

export const {
  useGetAllAlarmsQuery,
  useGetFilingsDashboardQuery,
  useGetAdminMissingInfoQuery,
  useGetAlarmRulesQuery,
  useCreateAlarmRuleMutation,
  useUpdateAlarmRuleMutation,
} = alarmApis;
